import { useState, useCallback } from 'react';
import { Button } from '@/components/ui/Button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import type { ML8OptionLetter } from '@/config/instruments';
import { ML_8_ITEMS } from '@/config/instruments';

export interface ML8RunnerProps {
  commercialName: string;
  onComplete: (choices: ML8OptionLetter[]) => void;
  initialChoices?: ML8OptionLetter[];
}

const INSTRUCTION =
  'Instrucciones: En cada pregunta, elige la opción que más se parezca a lo que prefieres en un trabajo.';

export function ML8Runner({
  commercialName,
  onComplete,
  initialChoices = [],
}: ML8RunnerProps) {
  const pad8 = (arr: ML8OptionLetter[]) =>
    [...arr, ...(Array(8 - arr.length).fill(undefined) as (ML8OptionLetter | undefined)[])].slice(0, 8);
  const [choices, setChoices] = useState<(ML8OptionLetter | undefined)[]>(() => pad8(initialChoices));
  const [currentIndex, setCurrentIndex] = useState(0);

  const setChoice = useCallback((idx: number, letter: ML8OptionLetter) => {
    setChoices((prev) => {
      const next = [...prev];
      next[idx] = letter;
      return next;
    });
  }, []);

  const item = ML_8_ITEMS[currentIndex];
  const choice = choices[currentIndex];

  const allComplete = ML_8_ITEMS.every((_, i) => choices[i] != null);

  const handleNext = () => {
    if (currentIndex < ML_8_ITEMS.length - 1) {
      setCurrentIndex((i) => i + 1);
    } else if (allComplete) {
      onComplete(choices.filter((x): x is ML8OptionLetter => x != null) as ML8OptionLetter[]);
    }
  };

  const handleSubmit = () => {
    if (!allComplete) return;
    onComplete(choices.filter((x): x is ML8OptionLetter => x != null) as ML8OptionLetter[]);
  };

  if (!item) return null;

  return (
    <Card className="max-w-2xl mx-auto">
      <CardHeader>
        <CardTitle className="text-lg">{commercialName}</CardTitle>
        <p className="text-sm text-muted-foreground">
          Pregunta {currentIndex + 1} de {ML_8_ITEMS.length}
        </p>
      </CardHeader>
      <CardContent className="space-y-6">
        {currentIndex === 0 && (
          <p className="text-sm text-muted-foreground leading-relaxed">
            {INSTRUCTION}
          </p>
        )}
        <div className="flex flex-col gap-2">
          {item.options.map((opt) => (
            <label
              key={opt.letter}
              className="flex cursor-pointer items-center gap-2 rounded-md border p-3 hover:bg-muted/50"
            >
              <input
                type="radio"
                name={item.id}
                checked={choice === opt.letter}
                onChange={() => setChoice(currentIndex, opt.letter)}
              />
              <span className="text-sm">
                <strong>{opt.letter})</strong> {opt.text}
              </span>
            </label>
          ))}
        </div>
        <div className="flex justify-between">
          <Button
            type="button"
            variant="outline"
            disabled={currentIndex === 0}
            onClick={() => setCurrentIndex((i) => i - 1)}
          >
            Anterior
          </Button>
          {currentIndex < ML_8_ITEMS.length - 1 ? (
            <Button
              type="button"
              onClick={handleNext}
              disabled={!choice}
            >
              Siguiente
            </Button>
          ) : (
            <Button type="button" onClick={handleSubmit} disabled={!allComplete}>
              Finalizar prueba
            </Button>
          )}
        </div>
        <p className="text-xs text-muted-foreground">
          Respondidas: {choices.filter((c) => c != null).length} / {ML_8_ITEMS.length}
        </p>
      </CardContent>
    </Card>
  );
}
